import React, { useState, useEffect } from 'react'
import { Box, FormControl, Button, Typography, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import DatePicker, { registerLocale } from 'react-datepicker'
import ja from 'date-fns/locale/ja'
import 'react-datepicker/dist/react-datepicker.css'
// components
import { BackButton } from '~/src/components/atoms/Button/BackButton/BackButton'
import { ProtectedPage } from '~/src/components/templates'
import { CardWithActionButton } from '~/src/components/organisms'
import { useMessage } from '~/src/hooks'
import { TNotificationBody, TNotificationItem } from '~/src/repositories/Notification'
import { notificationRepository } from '~/src/repositories'
import { formatDate } from '~/src/libs'

const notificationSchema = yup.object({
  date: yup.date().required(),
  message: yup.string().required(),
})

/**
 * NotificationDetail Page
 * @param __namedParameters
 * @returns JSX.Element
 * @category ReactComponents
 */
export const NotificationDetail: React.VFC = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState<boolean>(false)
  registerLocale('ja', ja)
  const { handleOpenWithErrorMessage, handleOpenWithSuccessMessage } = useMessage()
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<TNotificationBody>({
    resolver: yupResolver(notificationSchema),
  })

  const id = searchParams.get('id')
  const location = useLocation()
  // @ts-ignore
  const statement = location.state.item as TNotificationItem | null
  const textFieldStyle = {
    '& label': {
      fontSize: '1rem',
    },
    '& .MuiOutlinedInput-root': {
      fontSize: '1rem',
      lineHeight: '24px',
    },
    '& .MuiOutlinedInput-input': {
      fontSize: '1rem',
      lineHeight: '24px',
      paddingY: '8px',
      placeholder: {
        fontSize: '1rem',
      },
    },
  }

  useEffect(() => {
    if (!id || !statement) {
      return navigate('/admin/news/list', { replace: true })
    }
    if (statement) {
      setValue('message', statement.message)
      setValue('date', statement.openDate)
    }
  }, [id])

  /**
   * updateHandler
   * @param v {TNotificationBody}
   */
  const updateHandler = async (v: TNotificationBody) => {
    const id: string | null = searchParams.get('id')
    if (!id || id == '') {
      return handleOpenWithErrorMessage(t('notification.id_empty_error'))
    }
    if (!v.date || !v.message) {
      return handleOpenWithErrorMessage('select user')
    }
    setLoading(true)
    try {
      const payload: TNotificationBody = {
        // id: Number(id),
        date: formatDate(v.date, 'yyyy-MM-dd'),
        message: v.message,
      }
      await notificationRepository.patchNotification(Number(id), payload)
      handleOpenWithSuccessMessage('Success!!')
    } catch (e: any) {
      handleOpenWithErrorMessage(e.message)
    } finally {
      setLoading(false)
    }
  }

  /**
   * deleteHandler
   */
  const deleteHandler = async () => {
    const id: string | null = searchParams.get('id')
    if (!id || id == '') {
      return handleOpenWithErrorMessage(t('notification.id_empty_error'))
    }
    setLoading(true)
    try {
      await notificationRepository.deleteNotification(Number(id))
      handleOpenWithSuccessMessage('Success!!')
      navigate('/admin/news/list', { replace: true })
    } catch (e: any) {
      handleOpenWithErrorMessage(e.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ProtectedPage pageTitle={t('page_title.news_list')}>
      <BackButton location={location} />
      <form onSubmit={handleSubmit(updateHandler)}>
        <CardWithActionButton
          loading={loading}
          cardContentPadding="16px"
          otherActionComponents={
            <Button color="error" onClick={deleteHandler} sx={{ fontSize: '15px' }}>
              {t('notification.delete')}
            </Button>
          }
        >
          <Box
            display={'flex'}
            flexDirection={'row'}
            alignItems={'center'}
            component="dl"
            marginTop={0}
            marginBottom={'20px'}
          >
            <Box minWidth={'130px'} component="dt">
              <Typography variant="overline" sx={{ fontSize: '14px' }}>
                {t('notification.date')}
              </Typography>
            </Box>
            <Box component="dd" width={'100%'} marginLeft={4}>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name={'date'}
                  render={({ field: { onChange, value } }) => {
                    console.log('value', value)
                    return (
                      <DatePicker
                        dateFormat="yyyy-MM-dd"
                        onChange={onChange}
                        selected={value ? new Date(value) : undefined}
                        locale="ja"
                        placeholderText="20XX/XX/XX"
                        withPortal
                        customInput={
                          <TextField
                            sx={{
                              width: '260px',
                              ...textFieldStyle,
                            }}
                            size={'small'}
                            placeholder="20XX/XX/XX"
                            error={Boolean(errors.date)}
                            helperText={errors.date && errors.date.message}
                            {...register('date')}
                          />
                        }
                      />
                    )
                  }}
                />
              </FormControl>
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} alignItems={'top'} component="dl" margin={0}>
            <Box minWidth={'130px'} component="dt" marginTop={1.5}>
              <Typography variant="overline" sx={{ fontSize: '14px' }}>
                {t('notification.message')}
              </Typography>
            </Box>
            <Box component="dd" width={'100%'} marginLeft={4}>
              <FormControl fullWidth sx={{}}>
                <TextField
                  multiline
                  rows={5.2}
                  sx={{
                    '.MuiOutlinedInput-root': {
                      fontSize: '1rem',
                      padding: '8px 12px',
                    },
                  }}
                  placeholder={t('notification.message_placeholder')}
                  {...register('message')}
                />
              </FormControl>
            </Box>
          </Box>
        </CardWithActionButton>
      </form>
    </ProtectedPage>
  )
}
